import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { SidebarProvider } from './layout/SidebarContext';
import { AppLayout } from './layout/AppLayout';
import { IconPackProvider } from './icons/IconPackContext';
import { ThemeProvider } from './theme/ThemeContext';
import { SitesPage } from './pages/SitesPage';
import { SitePage } from './pages/SitePage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';
import { DiagramTooltipProvider } from './tooltips/DiagramTooltipContext';
import { AuthProvider, useAuth } from './auth/AuthContext';

function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, user } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070b14] text-sm text-white/70">
        Завантаження…
      </div>
    );
  }
  if (user) return children;
  try {
    sessionStorage.removeItem('ukrop-nm.guest');
  } catch {
    /* ignore */
  }
  const next = encodeURIComponent(`${location.pathname}${location.search}`);
  return <Navigate to={`/login?next=${next}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const authPage = location.pathname === '/login' || location.pathname === '/register';
  if (authPage) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage mode="login" />} />
        <Route path="/register" element={<LoginPage mode="register" />} />
      </Routes>
    );
  }

  return (
    <RequireAuth>
      <AppLayout>
        <Routes>
          <Route path="/" element={<SitesPage />} />
          <Route path="/site/:id/*" element={<SitePage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </AppLayout>
    </RequireAuth>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <IconPackProvider>
          <SidebarProvider>
            <DiagramTooltipProvider>
              <AppRoutes />
            </DiagramTooltipProvider>
          </SidebarProvider>
        </IconPackProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
